"use client";

import {
  COOKIE_KEY_HLW_CONSENT,
  useCookie,
  useRequestGeolocation,
  useRequestMe,
  useSession,
} from "@packages/sdk";
import type { ComponentProps } from "react";
import { useEffect, useState } from "react";
import { z } from "zod";

import { DEFAULT_CONSENT_OPTIONS_STRICT } from "../constants/defaultConsentOptionsStrict.constants";
import type { ConsentContext } from "../lib/consentContext";
import { consentContext } from "../lib/consentContext";
import { Consent } from "../types/enums/consent.enum";
import { handleConsentFromGeolocation } from "../utils/handleConsentFromGeolocation";

export type ConsentProviderProps = Omit<
  ComponentProps<typeof consentContext.Provider>,
  "value"
>;

export const ConsentProvider = ({
  children,
  ...props
}: ConsentProviderProps) => {
  const [consent, setConsent] = useCookie<number[]>({
    key: COOKIE_KEY_HLW_CONSENT,
    schema: z.number().array(),
  });
  const { query: queryGeolocation } = useRequestGeolocation();
  const { mutationPut: mutationPutMe, query: queryMe } = useRequestMe();
  const { status } = useSession();
  const [countryCode, setCountryCode] = useState<string | null>("US");
  const [isError, setIsError] = useState(false);

  const isConsenting =
    Array.isArray(consent) && consent.includes(Consent.ANALYTICS);
  const isLoading = !consent;

  /** TODO: remove/improve when ConsentModal/CookieManager are improved */
  const [isConsentModalVisible, setIsConsentModalVisible] = useState(false);
  const saveConsent = async ({
    consent,
  }: {
    consent: ConsentContext["consent"];
  }) => {
    if (status === "authenticated") {
      await mutationPutMe.mutateAsync({ consent });
    }
    setConsent(consent);
  };

  /**
   * If the user does not have consent preferences
   */
  useEffect(() => {
    if (
      (!consent ||
        !(
          Array.isArray(consent) &&
          (consent?.length === 0 ||
            consent?.every((c) => typeof c === "number"))
        )) &&
      !queryGeolocation.isLoading
    ) {
      handleConsentFromGeolocation({
        geolocation: queryGeolocation.data,
        setConsent,
        setCountryCode,
        setIsConsentModalVisible,
      });

      let gpc = false;
      if (typeof navigator !== "undefined") {
        // @ts-ignore -- this is what happens when you implement things that aren't really standards
        gpc = !!navigator.globalPrivacyControl;
      }

      if (gpc) setConsent(DEFAULT_CONSENT_OPTIONS_STRICT);
    }
  }, [consent, queryGeolocation.data, queryGeolocation.isLoading]);

  /** Sync any inconsistent consents */
  useEffect(() => {
    if (queryMe.data?.consent) {
      if (
        (consent &&
          JSON.stringify(consent?.sort()) !==
            JSON.stringify(queryMe.data.consent.sort())) ||
        !consent
      ) {
        setConsent(queryMe.data.consent);
      }
    } else if (consent && status === "authenticated") {
      mutationPutMe.mutate({ consent });
    }
  }, [consent, queryMe.data?.consent]);

  return (
    <consentContext.Provider
      value={{
        consent,
        isConsenting,
        isLoading,
        isError,
        isConsentModalVisible,
        countryCode,
        saveConsent,
        setConsent,
        setCountryCode,
        setIsError,
        setIsConsentModalVisible,
      }}
      {...props}
    >
      {children}
    </consentContext.Provider>
  );
};
